import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { BlankFileIcon, VideoIcon } from '@tyro/icons';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDocumentationPages } from '../../api/pages';
import {
  useReaderActions,
  useReaderState,
} from '../../store/documentation-reader-provider';
import { buildTree, ReaderPageItem } from './reader-page-item';

type ReaderTypeFilter = 'all' | 'article' | 'video';

export function ReaderPageList() {
  const { t } = useTranslation(['documentation']);

  const { navigate } = useReaderActions();
  const { currentSpace } = useReaderState();

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [typeFilter, setTypeFilter] = useState<ReaderTypeFilter>('all');

  const { data: pages, isLoading } = useDocumentationPages(
    currentSpace?.id ?? 0,
  );

  const publishedPages = useMemo(
    () => pages?.filter((page) => page.publishedVersionId != null) ?? [],
    [pages],
  );

  const articleCount = currentSpace?.articleCount ?? 0;
  const videoCount = currentSpace?.videoCount ?? 0;

  const filteredPages = useMemo(() => {
    if (typeFilter === 'article')
      return publishedPages.filter((page) => page.entityType !== 'video');
    if (typeFilter === 'video')
      return publishedPages.filter((page) => page.entityType === 'video');
    return publishedPages;
  }, [publishedPages, typeFilter]);

  const tree = useMemo(() => buildTree(filteredPages), [filteredPages]);
  const subPageCount = useMemo(
    () => publishedPages.filter((page) => page.parentPageId != null).length,
    [publishedPages],
  );

  const handleToggle = useCallback((pageId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }, []);

  // Rendered only when a space is selected (the parent gates on it); the
  // guard narrows the nullable context value for the JSX below.
  if (!currentSpace) return null;
  const space = currentSpace;

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Stack>
      {(space.description || publishedPages.length > 0) && (
        <Stack sx={{ px: 3, pt: 2.5, pb: 2.5, gap: 0.5 }}>
          {space.description && (
            <Typography variant="body2" color="text.secondary">
              {space.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {t('documentation:reader.spaceMeta', {
              count: publishedPages.length,
            })}
            {subPageCount > 0 && (
              <>
                {' · '}
                {t('documentation:reader.subPagesCount', {
                  count: subPageCount,
                })}
              </>
            )}
          </Typography>
        </Stack>
      )}
      {videoCount > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 3, pb: 1.5, pt: 0.5, flexWrap: 'wrap' }}
        >
          <Chip
            label={`${t('documentation:reader.tabs.all')} (${publishedPages.length})`}
            size="small"
            onClick={() => setTypeFilter('all')}
            color={typeFilter === 'all' ? 'primary' : 'default'}
            variant={typeFilter === 'all' ? 'filled' : 'outlined'}
            sx={{ height: 24, fontSize: 12 }}
          />
          <Chip
            icon={<BlankFileIcon sx={{ fontSize: 14 }} />}
            label={`${t('documentation:reader.tabs.articles')} (${articleCount})`}
            size="small"
            onClick={() => setTypeFilter('article')}
            color={typeFilter === 'article' ? 'primary' : 'default'}
            variant={typeFilter === 'article' ? 'filled' : 'outlined'}
            sx={{ height: 24, fontSize: 12 }}
          />
          <Chip
            icon={<VideoIcon sx={{ fontSize: 14 }} />}
            label={`${t('documentation:reader.tabs.videos')} (${videoCount})`}
            size="small"
            onClick={() => setTypeFilter('video')}
            color={typeFilter === 'video' ? 'primary' : 'default'}
            variant={typeFilter === 'video' ? 'filled' : 'outlined'}
            sx={{ height: 24, fontSize: 12 }}
          />
        </Stack>
      )}
      <Divider />
      {tree.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}
        >
          {t('documentation:noPages')}
        </Typography>
      ) : (
        <Stack>
          {tree.map((node, index) => (
            <Fragment key={node.page.id}>
              {index > 0 && <Divider />}
              <ReaderPageItem
                node={node}
                level={0}
                expanded={expanded}
                onToggle={handleToggle}
                onSelectPage={() =>
                  navigate({
                    view: 'page',
                    spaceId: space.id,
                    pageSlug: node.page.slug,
                  })
                }
              />
            </Fragment>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
